import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, ExternalLink, Trash2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';

export default function CompetitionPagesListPage() {
  const [pages, setPages] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pageToDelete, setPageToDelete] = useState(null);
  
  const fetchPages = () => {
    axios.get(`${API}/competition-pages`, { withCredentials: true })
      .then(res => setPages(res.data))
      .catch(err => console.error(err));
  };
  
  useEffect(() => {
    fetchPages();
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(res => setCurrentUser(res.data))
      .catch(() => setCurrentUser(null));
  }, []);
  
  const handleDeleteClick = (page, e) => {
    e.preventDefault();
    e.stopPropagation();
    setPageToDelete(page);
    setDeleteDialogOpen(true);
  };
  
  const handleConfirmDelete = async () => {
    if (!pageToDelete) return;
    
    setDeletingId(pageToDelete.page_id);
    try {
      await axios.delete(`${API}/competition-pages/${pageToDelete.page_id}`, { withCredentials: true });
      toast.success('Page deleted');
      fetchPages();
    } catch (error) {
      toast.error('Failed to delete page');
    } finally {
      setDeletingId(null);
      setDeleteDialogOpen(false);
      setPageToDelete(null);
    }
  };
  
  const getStatusColor = (status) => {
    switch(status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };
  
  return (
    <div className="space-y-6 fade-in">
      <div className="flex items-center justify-between">
        <h1 className="text-4xl font-bold text-[#2D4A3E]">
          Competition Pages
        </h1>
        <Link to="/competition-pages/create">
          <Button className="btn-primary" data-testid="create-page-button">
            <Plus className="mr-2 h-5 w-5" />
            New Page
          </Button>
        </Link>
      </div>
      
      <p className="text-muted-foreground">
        Create and manage public-facing pages for your competitions. Pages must be approved before they become visible to the public.
      </p>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {pages.map(page => (
          <Card key={page.page_id} className="p-6 card-hover" data-testid={`page-card-${page.page_id}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-stone-800">{page.title}</h3>
              </div>
              <Badge className={getStatusColor(page.status)}>{page.status}</Badge>
            </div>
            
            {page.content && (
              <div 
                className="prose prose-sm mb-4 line-clamp-2 text-stone-600" 
                dangerouslySetInnerHTML={{ __html: page.content }} 
              />
            )}
            
            <div className="flex items-center gap-2 mt-4">
              <Link to={`/competition-pages/${page.page_id}`} className="flex-1">
                <Button variant="outline" className="w-full" data-testid={`view-page-${page.page_id}`}>
                  Edit
                </Button>
              </Link>

              {/* Super-user shortcut: send them straight to the preview
                  page (with Approve/Reject in its banner) so they can
                  action a pending page without hunting for it in
                  /admin/approvals. */}
              {page.status === 'pending' && currentUser?.role === 'super' && (
                <Link to={`/admin/preview/${page.page_id}`}>
                  <Button
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid={`review-page-${page.page_id}`}
                  >
                    <ShieldCheck className="mr-2 h-4 w-4" />
                    Review
                  </Button>
                </Link>
              )}

              {page.status === 'approved' && (
                <a 
                  href={`/competition/${page.page_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="outline" size="icon" title="View Live Page">
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </a>
              )}
              
              <Button 
                variant="ghost" 
                size="icon"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={(e) => handleDeleteClick(page, e)}
                disabled={deletingId === page.page_id}
                title="Delete Page"
                data-testid={`delete-page-${page.page_id}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </Card>
        ))}
      </div>
      
      {pages.length === 0 && (
        <div className="text-center py-12">
          <p className="text-muted-foreground">No competition pages yet. Use the button above to create your first one!</p>
        </div>
      )}
      
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleConfirmDelete}
        itemName={pageToDelete?.title ? `"${pageToDelete.title}"` : 'this page'}
        loading={deletingId !== null}
      />
    </div>
  );
}
